import React from 'react';
import { Column } from 'react-table';
import SessionTable, {
  ResponseGuard,
  SessionTableInstanceProps
} from '../index';
import { YesNoCell, CellFunction, NAGuard, NoneGuard } from '../../cells';
import Tooltip from '../../../tooltip';
import Icon from '../../../../atoms/icon';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';

import { IEpxDiabetes } from '../../../../../libs/apps/epxdiabetes';
import { useMetaData } from '../../../../../contexts/metadata';
import moment from 'moment';

type Dependencies = [boolean, boolean, boolean, boolean];

const getDependencies = (sessions: IEpxDiabetes[]): Dependencies => {
  let usesSymptom: boolean;
  let usesActionPlan: boolean;
  let usesPatientOk: boolean;
  let usesEnoughSupplies: boolean;

  const schedule = sessions[0]?.schedule;
  if (schedule?.askSymptoms === true) {
    usesSymptom = true;
  }
  if (schedule?.askActionPlan === true) {
    usesActionPlan = true;
  }
  if (schedule?.isCheckupEnabled === true) {
    usesPatientOk = true;
    usesEnoughSupplies = true;
  }

  return [usesSymptom, usesActionPlan, usesPatientOk, usesEnoughSupplies];
};

interface HeaderProps {
  title: string;
  content?: string;
}

const RegularHeader = ({ title, content }: HeaderProps) => {
  return (
    <Tooltip content={content}>
      <span>
        {title}
        <Icon className="ml-2" icon={faInfoCircle} />
      </span>
    </Tooltip>
  );
};

const CheckupHeader = ({ title }: HeaderProps) => {
  const { terms } = useMetaData();
  const content = `This column will only have values if the ${terms.patient.default} is unresponsive and received a checkup call/text, and then responded to the checkup questions.`;
  return <RegularHeader title={title} content={content} />;
};

const CriticalHeader = ({ title }: HeaderProps) => {
  const { terms } = useMetaData();
  const content = `This column will only have values if the ${terms.patient.default} entered a critical blood sugar, and responded accordingly.`;
  return <RegularHeader title={title} content={content} />;
};

const BloodSugarCell: CellFunction<IEpxDiabetes> = ({
  row: { original: session }
}) => {
  const recheckLabel = session.isRecheck ? ' (Recheck)' : '';
  const invalidated = session?.invalidation?.key === 'bloodSugar';
  const newValue = session?.invalidation?.newValue;
  const invalidationDate = session?.invalidation?.createdAt
    ? moment(session.invalidation.createdAt).format('MMM D, YYYY h:mm A')
    : null;
  const prevValue = session?.invalidation?.previousValue;

  if (invalidated) {
    const tooltipText = `This blood sugar value was invalidated on ${
      invalidationDate || 'unknown date'
    } and the Original value was ${prevValue ?? '?'}`;
    return (
      <Tooltip content={tooltipText} position="top">
        <span>
          {newValue != null ? newValue : '?'}*{recheckLabel}
        </span>
      </Tooltip>
    );
  } else if (session.bloodSugar != null) {
    return <span>{`${session.bloodSugar}${recheckLabel}`}</span>;
  }

  return (
    <span>
      {NoneGuard(session.bloodSugar)}
      {recheckLabel}
    </span>
  );
};

const GetNACheckup = () => {
  return (session: IEpxDiabetes): boolean => {
    return session.checkup === false || session.checkup === null;
  };
};

const GetNARegular = () => {
  return (session: IEpxDiabetes): boolean => {
    return session.checkup === true;
  };
};

const GetNARecheck = () => {
  return (session: IEpxDiabetes): boolean => {
    return session.isRecheck === true;
  };
};

const getColumns = (sessions: IEpxDiabetes[]): Column<IEpxDiabetes>[] => {
  const [usesSymptom, usesActionPlan, usesPatientOk, usesEnoughSupplies] =
    getDependencies(sessions);
  const columns: Column<IEpxDiabetes>[] = [
    {
      Header: (
        <RegularHeader
          title="Blood Sugar"
          content="This column may be N/A if the session was a checkup call or text."
        />
      ),
      accessor: 'bloodSugar',
      Cell: NAGuard(ResponseGuard(BloodSugarCell), GetNARegular())
    },
    {
      Header: (
        <RegularHeader
          title="Has Eaten"
          content="This column may be N/A if the session was a checkup call or text, or if it was a recheck session."
        />
      ),
      accessor: 'hasEaten',
      Cell: NAGuard(
        NAGuard(
          ResponseGuard(YesNoCell('hasEaten') as CellFunction<IEpxDiabetes>),
          GetNARegular()
        ),
        GetNARecheck()
      )
    }
  ];
  if (usesSymptom) {
    columns.push({
      Header: <CriticalHeader title="Has Symptoms" />,
      accessor: 'hasSymptoms',
      Cell: NAGuard(
        ResponseGuard(YesNoCell('hasSymptoms') as CellFunction<IEpxDiabetes>),
        GetNARegular()
      )
    });
  }
  if (usesActionPlan) {
    columns.push({
      Header: <CriticalHeader title="Has Action Plan" />,
      accessor: 'hasActionPlan',
      Cell: NAGuard(
        ResponseGuard(YesNoCell('hasActionPlan') as CellFunction<IEpxDiabetes>),
        GetNARegular()
      )
    });
  }
  if (usesPatientOk) {
    columns.push({
      Header: <CheckupHeader title="Is Patient OK" />,
      accessor: 'patientOk',
      Cell: NAGuard(
        ResponseGuard(YesNoCell('patientOk') as CellFunction<IEpxDiabetes>),
        GetNACheckup()
      )
    });
  }
  if (usesEnoughSupplies) {
    columns.push({
      Header: <CheckupHeader title="Has Enough Supplies" />,
      accessor: 'enoughSupplies',
      Cell: NAGuard(
        ResponseGuard(
          YesNoCell('enoughSupplies') as CellFunction<IEpxDiabetes>
        ),
        GetNACheckup()
      )
    });
  }
  return columns;
};

const EpxDiabetesTable: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  return (
    <SessionTable<IEpxDiabetes>
      url="/epxdiabetes/data/"
      getColumns={getColumns}
      getDependencies={getDependencies}
      appKey="epxdiabetes"
      invalidationModel="EpxDiabetes"
      {...props}
    />
  );
};

export default EpxDiabetesTable;
